import { useEffect, useRef, useState } from 'react';
import { testimonialsContent } from '@/mocks/home';

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function TestimonialsSection() {
  const { ref: sectionRef, inView } = useInView<HTMLDivElement>();

  return (
    <section className="relative bg-background-100 py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
        {/* Title */}
        <div className={`text-center mb-10 md:mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
            {testimonialsContent.titleEn}
          </span>
          <h2 className="mt-1 text-2xl md:text-3xl lg:text-4xl font-black text-foreground-50">
            {testimonialsContent.titleCn}
          </h2>
          <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full mx-auto" />
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {testimonialsContent.items.map((item, index) => (
            <div
              key={item.id}
              className={`bg-background-50 rounded-lg border border-background-200/50 p-5 md:p-6 transition-all duration-500 hover:border-primary-500/30 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${150 + index * 100}ms` }}
            >
              {/* Quote icon */}
              <div className="w-8 h-8 flex items-center justify-center rounded bg-background-100 text-foreground-400 mb-4">
                <i className="ri-double-quotes-l text-base" />
              </div>

              {/* Content */}
              <p className="text-sm text-foreground-200 leading-relaxed italic">
                {item.content}
              </p>

              {/* Divider */}
              <div className="mt-4 pt-4 border-t border-background-200/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground-100">{item.company}</p>
                    <p className="text-xs text-foreground-400">{item.role}</p>
                  </div>
                  <span className="text-xs text-primary-400 bg-primary-950/30 px-2 py-1 rounded-full font-medium">
                    已上线
                  </span>
                </div>
                <p className="mt-2 text-xs text-foreground-500">{item.scene}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}